import { useQueryClient } from '@tanstack/react-query'
import { FileText, GitBranch, Upload } from 'lucide-react'
import { useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useOutletContext } from 'react-router-dom'
import { DocumentList } from '@/components/documents/DocumentList'
import { SchemaSetupDialog } from '@/components/documents/SchemaSetupDialog'
import { PageShell } from '@/components/layout/PageShell'
import { Button } from '@/components/ui/button'
import { useCaseDocumentsPage } from '@/features/cases/hooks/useCaseDocumentsPage'
import { CaseDocumentUploader } from '@/features/documents/CaseDocumentUploader'
import { useSchemaUploadReminder } from '@/features/documents/hooks/use-schema-upload-reminder'
import {
  structuredIngestKeys,
  usePrepareMappings,
  useStructuredIngestList,
  useStructuredIngestReadiness,
} from '@/features/structured-ingest/hooks/use-structured-ingest'
import { mappingsByDocument } from '@/services/api/structuredIngest'
import { useCaseContext } from '../../CaseContext'
import type { CaseOutletContext } from '../../CaseLayout'

export default function DocumentsListPage() {
  const { t } = useTranslation()
  const { caseId, caseData, refetchCase } = useCaseContext()
  const { handleViewGeoData } = useOutletContext<CaseOutletContext>()
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  const {
    selectedDocId,
    uploadDialogOpen,
    setUploadDialogOpen,
    currentFolderPath,
    setCurrentFolderPath,
    docListRefreshKey,
    handleRowSelect,
    bumpDocListRefresh,
  } = useCaseDocumentsPage({ refetchCase })

  // Tabular uploads get a structured mapping proposal in the background; the
  // rows show its state and open the review screen.
  const mappingsQuery = useStructuredIngestList(caseId)
  const mappings = useMemo(() => mappingsByDocument(mappingsQuery.data ?? []), [mappingsQuery.data])
  const openMapping = useCallback(
    (analysisId: string) =>
      navigate(`/cases/${caseId}/structured-ingest?analysis=${encodeURIComponent(analysisId)}`),
    [navigate, caseId]
  )
  // A table without a mapping (uploaded before the case schema was accepted, or imported) offers
  // to start one from its row, once the case's mappings are known and the schema allows it.
  const readiness = useStructuredIngestReadiness(caseId)
  const prepareMappings = usePrepareMappings(caseId)
  const canPrepareMappings = mappingsQuery.isSuccess && readiness.data?.ready === true
  const handleUploadComplete = useCallback(() => {
    bumpDocListRefresh()
    void queryClient.invalidateQueries({ queryKey: structuredIngestKeys.list(caseId) })
  }, [bumpDocListRefresh, queryClient, caseId])

  const isEmptyCase = (caseData.document_count ?? 0) === 0
  const schemaReminder = useSchemaUploadReminder({
    caseId,
    hasDocuments: !isEmptyCase,
    uploadDialogOpen,
  })

  const emptyHero = (
    <div className="flex flex-1 flex-col items-center justify-center px-6 py-16 text-center">
      <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-primary/10 text-primary">
        <Upload className="size-7" />
      </div>
      <h2 className="mt-4 text-lg font-semibold text-foreground">
        {t('cases.documents.empty.title')}
      </h2>
      <p className="mt-1 max-w-md text-sm text-muted-foreground">
        {t('cases.documents.empty.body')}
      </p>
      <div className="mt-6">
        <Button
          variant="default"
          size="sm"
          onClick={() => setUploadDialogOpen(true)}
          className="inline-flex items-center gap-1.5"
        >
          <Upload className="size-3.5" />
          {t('cases.documents.uploadFiles')}
        </Button>
      </div>
    </div>
  )

  return (
    <PageShell
      layout="full"
      icon={FileText}
      title={t('cases.documents.title')}
      summary={isEmptyCase ? undefined : t('cases.documents.summary')}
      toolbar={
        isEmptyCase ? null : (
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate(`/cases/${caseId}/extraction-runs`)}
              className="inline-flex h-9 items-center gap-1 px-2 text-xs font-medium md:h-7"
            >
              <GitBranch className="size-3" /> {t('cases.documents.viewExtractionRuns')}
            </Button>
            <Button
              variant="default"
              size="sm"
              onClick={() => setUploadDialogOpen(true)}
              className="inline-flex h-9 items-center gap-1 px-2 text-xs font-medium md:h-7"
            >
              <Upload className="size-3" /> {t('cases.documents.uploadFiles')}
            </Button>
          </div>
        )
      }
    >
      {isEmptyCase ? (
        emptyHero
      ) : (
        <div className="flex flex-1 overflow-hidden bg-background">
          <div className="w-full overflow-hidden">
            <DocumentList
              caseId={caseId}
              onViewGeoData={handleViewGeoData}
              onDocumentSelect={handleRowSelect}
              selectedDocId={selectedDocId}
              onPathChange={setCurrentFolderPath}
              refreshKey={docListRefreshKey}
              mappings={mappings}
              onReviewMapping={openMapping}
              onPrepareMapping={canPrepareMappings ? prepareMappings.start : undefined}
              startingMappings={prepareMappings.starting}
            />
          </div>
        </div>
      )}

      <CaseDocumentUploader
        caseId={caseId}
        caseName={caseData.name}
        open={uploadDialogOpen}
        onOpenChange={setUploadDialogOpen}
        onUploadComplete={handleUploadComplete}
        basePath={currentFolderPath}
        onSwitchToLake={() => {
          setUploadDialogOpen(false)
          navigate(`/data?destination_case_id=${caseId}`)
        }}
      />
      <SchemaSetupDialog
        caseId={caseId}
        open={schemaReminder.open}
        onDismiss={schemaReminder.dismiss}
      />
    </PageShell>
  )
}
